import { ORDERSTATUS, ORDERTYPE } from "../../../models/order";

export type PromoFilter = {
  order_type?: string;
  order_status?: string;
};

export type StandalonePreset = {
  key: string;
  label: string;
  filter: PromoFilter;
  paramsOverride?: Record<string, any>;
  privilegedOnly?: boolean;
  /**
   * @deprecated No longer read. Admin and picker now both render the standard
   * OrdersContainer for the Manage preset; the separate handling container is
   * kept but unused. Left in place to avoid breaking the preset definitions.
   */
  rendersHandling?: boolean;
};

export const HANDLE_PRESET_KEY = "handle";
export const APPROVE_PRESET_KEY = "approve";
export const SEND_PRESET_KEY = "send";
// Promo always lands on Backorder by default; the "all" overview preset is a
// separate, explicitly-selected button (it does not change the default landing).
export const DEFAULT_PRESET_KEY = "backorder";

// Page size for a *client-side full load*: one request that pulls every matching
// order together with its order items, products and sub-orders, so a selection
// survives paging (see OrdersTable's clientPaginate).
//
// ⚠️ This is only safe on a list that is already narrow. The flow presets are
// narrow by construction (one order type + one status). On a broad overview it
// is not: a promo code on the "All" preset still matches every Original (an
// original carries 90+ order items) and every backorder, for every store — one
// payload of thousands of nested orders, which locks the browser up. The gate
// lives in PromoOrdersView (`fullLoadAllowed`); keep the two in sync.
export const BULK_FETCH_LIMIT = 10000;

export const TYPE_GROUPS: {
  prefix: string;
  label: string;
  orderType: string;
}[] = [
  { prefix: "backorder", label: "Backorder", orderType: ORDERTYPE.backorder },
];

export const STATUS_OPTIONS: {
  key: string;
  label: string;
  status?: string;
  privileged?: boolean;
}[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending", status: ORDERSTATUS.pending },
  { key: "ordered", label: "Ordered", status: ORDERSTATUS.ordered },
  {
    key: "in-progress",
    label: "In progress",
    status: ORDERSTATUS.inProgress,
    privileged: true,
  },
  { key: "shipped", label: "Shipped", status: ORDERSTATUS.shipped },
  { key: "cancelled", label: "Cancelled", status: ORDERSTATUS.cancelled },
];

export const STANDALONE_PRESETS: StandalonePreset[] = [
  {
    key: "backorder",
    label: "Backorder",
    filter: { order_type: ORDERTYPE.backorder },
  },
  {
    key: "original",
    label: "Original",
    filter: {
      order_type: ORDERTYPE.original,
      order_status: ORDERSTATUS.ordered,
    },
  },
  {
    // Overview of every promo order — no type/status filter. Sits to the right
    // of Original; Backorder stays the default landing (see DEFAULT_PRESET_KEY).
    key: "all",
    label: "All",
    filter: {},
  },
  {
    key: HANDLE_PRESET_KEY,
    label: "Handle",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.ordered,
    },
    paramsOverride: { limit: BULK_FETCH_LIMIT },
    privilegedOnly: true,
    rendersHandling: true,
  },
  {
    key: APPROVE_PRESET_KEY,
    label: "Approve",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.pending,
    },
    paramsOverride: { limit: BULK_FETCH_LIMIT },
    privilegedOnly: true,
    rendersHandling: true,
  },
  {
    key: SEND_PRESET_KEY,
    label: "Send",
    filter: {
      order_type: ORDERTYPE.backorder,
      order_status: ORDERSTATUS.inProgress,
    },
    paramsOverride: { limit: BULK_FETCH_LIMIT },
    privilegedOnly: true,
  },
];

export const PROMO_BASE_PARAMS = {
  limit: 50,
  sort: JSON.stringify({ column: "order_date", order: "desc" }),
  include_sub_orders: 1,
};

/**
 * Is the current list narrow enough to be pulled in one client-side full load
 * (and therefore to carry a row selection across pages)?
 *
 * Requires a promo code — lists are requested per promo — **plus** one more
 * axis: a single order type or a single status. A promo code on its own does
 * not bound anything: on the "All" overview it still matches every original
 * and every backorder for every store, which is the request that used to lock
 * the portal up when a filter was removed.
 *
 * On the Backorder preset that second axis comes for free (the preset sets
 * order_type), so the per-promo Request-lists path is unaffected. Originals are
 * excluded outright: they are never selectable (OrdersTable drops them from
 * selectableItems), no bulk action applies to them, and they are the heaviest
 * rows in the set.
 *
 * The flow presets (Approve / Manage / Send) are narrow by construction and do
 * not go through this check.
 */
export function isNarrowEnoughForFullLoad(params: {
  promo_code?: string;
  order_type?: string;
  order_status?: string;
}): boolean {
  const hasPromoCode =
    typeof params.promo_code === "string" && params.promo_code !== "";
  const hasOrderType =
    typeof params.order_type === "string" && params.order_type !== "";
  const hasOrderStatus =
    typeof params.order_status === "string" && params.order_status !== "";
  const originalsOnly = params.order_type === ORDERTYPE.original;
  return hasPromoCode && (hasOrderType || hasOrderStatus) && !originalsOnly;
}

export function resolvePreset(key: string | null): {
  preset: StandalonePreset | null;
  presetKey: string;
  filter: PromoFilter;
} {
  if (key) {
    const dotIdx = key.indexOf(".");
    if (dotIdx > 0) {
      const prefix = key.slice(0, dotIdx);
      const subKey = key.slice(dotIdx + 1);
      const group = TYPE_GROUPS.find((g) => g.prefix === prefix);
      const sub = STATUS_OPTIONS.find((o) => o.key === subKey);
      if (group && sub) {
        const filter: PromoFilter = { order_type: group.orderType };
        if (sub.status) filter.order_status = sub.status;
        return { preset: null, presetKey: key, filter };
      }
    }
    const standalone = STANDALONE_PRESETS.find((p) => p.key === key);
    if (standalone)
      return {
        preset: standalone,
        presetKey: standalone.key,
        filter: standalone.filter,
      };
  }
  return resolvePreset(DEFAULT_PRESET_KEY);
}
